import { Outlet, useNavigate, useParams } from "react-router-dom"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import useSpan from "helpers/hooks/spans/useSpan"
import usePowerline from "helpers/hooks/powerlines/usePowerline"
import useDocumentTitle from "helpers/hooks/useDocumentTitle"
import useDateRange from "helpers/hooks/useDateRange"
import useRoutes from "helpers/hooks/useRoutes"
import { getSpanTabsConfig } from "helpers/config/tabs/spanTabsConfig"
import { getSpanDisplayName } from "helpers/utils/spans"
import { DEFAULT_TARGET } from "helpers/utils/constants"
import LinkTabsSection from "widgets/common/LinkTabsSection"
import Spinner from "widgets/common/Spinner"
import PreviousPage from "widgets/common/PreviousPage"
import ScrollToTopButton from "widgets/common/ScrollToTopButton"
import ExitZoomModeButton from "widgets/common/ExitZoomModeButton"
import DashboardDatePicker from "widgets/common/dashboard/DashboardDatePicker"
import SpanSummary from "widgets/spans/SpanSummary"

const Span = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { span, isFetching: spanFetching, isError } = useSpan({ id: Number(id) })

  // The mother line supplies the ordered span_ids, which the name fallback and
  // the prev/next controls both need.
  const { powerline, isLoading: powerlineLoading } = usePowerline({
    id: span?.powerline_id,
  })

  useDocumentTitle(
    span ? getSpanDisplayName(span, powerline?.span_ids, t) : t("pageTitles.SPAN"),
  )

  const { baseOrgURL } = useRoutes()
  const { getDateRangeTools, arePlotsLoading } = useDateRange()
  const {
    dateRange: spanDateRange,
    dispatchDateRange: dispatchSpanDateRange,
    searchParams: spanSearchParams,
  } = getDateRangeTools("span")
  const navigate = useNavigate()

  const spanTabsConfig = useMemo(
    () => getSpanTabsConfig(spanSearchParams),
    [spanSearchParams],
  )

  useEffect(() => {
    if (isError) {
      navigate(DEFAULT_TARGET)
    }
  }, [isError, navigate])

  return (
    <>
      {spanFetching || !span ? (
        <Spinner styles={{ my: 2 }} />
      ) : (
        <>
          <PreviousPage href={`${baseOrgURL}/powerlines/${span.powerline_id}`} />
          <SpanSummary
            span={span}
            powerline={powerline}
            powerlineLoading={powerlineLoading}
            actions={
              <DashboardDatePicker
                dateRange={spanDateRange}
                dispatchDateRange={dispatchSpanDateRange}
                entity="span"
                isFetching={arePlotsLoading}
              />
            }
          />
          <LinkTabsSection tabsConfig={spanTabsConfig} />
          <Outlet context={{ span, powerline }} />
          <ExitZoomModeButton entity="span" />
          <ScrollToTopButton />
        </>
      )}
    </>
  )
}

export default Span
